/**
 * The drawer's fixed option lists, and what a brand-new rule opens with — all that
 * remains of the prototype's `mock-data.ts` now the criteria live in `criteria.ts`.
 */
import { RuleDraft, TemplateScope } from './types'

/** The asset every rule here sits on, shaped like the app's asset so
 *  `WebsiteAvatar` can read it directly. */
export const SCOPE_OPTIONS = [
	// The label is replaced at the call site with the website's own name and favicon:
	// a saved rule has to say WHICH website it protects.
	{ value: 'website', label: 'This website' },
	{ value: 'accounts', label: 'Selected ad accounts' },
	{ value: 'campaigns', label: 'Selected campaigns' }
]

export const FOCUS_OPTIONS = [
	{ value: 'Visitor', label: 'Visitor' },
	{ value: 'IP address', label: 'IP address' },
	{ value: 'IP range', label: 'IP range' },
	{ value: 'Placement', label: 'Placement' },
	{ value: 'Device', label: 'Device' }
]

export const triggerOrdinal = (stored: number): string => {
	const number = stored + 1
	const lastTwo = number % 100
	const suffix =
		lastTwo >= 11 && lastTwo <= 13
			? 'th'
			: ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[
					number % 10
			  ] ?? 'th'
	return `${number}${suffix}`
}

export const validTriggerThreshold = (value: unknown): value is number =>
	typeof value === 'number' &&
	Number.isInteger(value) &&
	value >= 0 &&
	value <= 19

export const TRIGGER_OPTIONS = Array.from({ length: 20 }, (_, value) => ({
	value: String(value),
	label: `${triggerOrdinal(value)} click`
}))

export const PERIOD_OPTIONS = [
	{ value: 'minutes', label: 'minutes' },
	{ value: 'hours', label: 'hours' },
	{ value: 'days', label: 'days' }
]

export const CLICK_STATUS_OPTIONS = [
	{ value: 'Valid', label: 'Valid' },
	{ value: 'Suspicious', label: 'Suspicious' },
	{ value: 'Bad', label: 'Bad' }
]

/** What a brand new rule opens with: nothing decided, so the summary strip has no
 *  WHEN and no THEN to print yet — a prefilled verdict is a decision the form made
 *  for you. Nothing protective is pre-armed either. */
export const DEFAULT_RULE_DRAFT: RuleDraft = {
	name: '',
	description: '',
	scope: { mode: 'website', values: [] },
	trigger: {
		maxClicks: '',
		periodValue: '',
		periodUnit: 'hours',
		focus: ''
	},
	criteria: [],
	threshold: { mode: 'all', percent: '' },
	actions: {
		clickStatus: '',
		addLabels: false,
		labels: '',
		blacklist: false,
		blacklistValue: 24,
		blacklistUnit: 'hours',
		tempIpExclusion: false,
		audienceExclusion: false
	}
}

/** The library tiers, in picker order (yours → team). */
export const TEMPLATE_SCOPE_OPTIONS: {
	value: TemplateScope
	label: string
	hint: string
}[] = [
	{ value: 'mine', label: 'My rules', hint: 'Only you can use it.' },
	{
		value: 'shared',
		label: 'Shared rules',
		hint: 'Everyone in this workspace can use it.'
	}
]

/** Seeded template library — workspace and personal examples, so the picker
 *  demonstrates both scopes. Each `draft` is a full rule config minus the live link. */
